import React, { useState, useEffect, useRef } from "react";
import "./HeroSection.css";

// Updated data: exclusively focused on lap spools for Arklon
const HERO_DATA = [
  {
    id: "01",
    tag: "Lap Spools",
    title: "Arklon Lap Spools\nversatile Engineering Polyamide",
    desc: "High-tensile, wear-resistant lap spools engineered for textile excellence.",
    image: "/assets/emma2.png", // Replace with actual spool image
    link: "/products",
  },
  {
    id: "02",
    tag: "Arklon",
    title: "Arklon Lap Spools\nQuality\nUnmatched",
    desc: "Zero-tolerance quality controls for lap spools that last longer and perform better.",
    image: "/assets/emma1.png", // Replace with actual spool image
    link: "/products",
  },
  {
    id: "03",
    tag: "Industry Trust",
    title: "Arklon Lap Spools\nTrusted By\nIndustry Leaders",
    desc: "Custom-machined lap spools built for the world's most demanding textile environments.",
    image: "/assets/homeimg2.png", // Replace with actual spool image
    link: "/products",
  },
];

const SLIDE_DURATION = 7000;

// Helper function to add trademark symbol to Arklon
const addTrademarkToArklon = (text) => {
  // Replace Arklon with Arklon® (case sensitive)
  return text.replace(/Arklon/g, 'Arklon®');
};

export default function HeroSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [prevIndex, setPrevIndex] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [mounted, setMounted] = useState(false);
  const intervalRef = useRef(null);
  const progressRef = useRef(null);
  const startTimeRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 60);
    return () => clearTimeout(t);
  }, []);

  const goTo = (idx) => {
    if (idx === activeIndex || isAnimating) return;
    setIsAnimating(true);
    setPrevIndex(activeIndex);
    setActiveIndex(idx);
    setProgress(0);
    startTimeRef.current = performance.now();
    setTimeout(() => {
      setIsAnimating(false);
      setPrevIndex(null);
    }, 900);
  };

  // Progress bar animation
  useEffect(() => {
    startTimeRef.current = performance.now();

    const tick = (now) => {
      const elapsed = now - startTimeRef.current;
      const pct = Math.min((elapsed / SLIDE_DURATION) * 100, 100);
      setProgress(pct);
      if (pct < 100) {
        progressRef.current = requestAnimationFrame(tick);
      }
    };

    progressRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(progressRef.current);
  }, [activeIndex]);

  // Auto-advance
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setActiveIndex((prev) => {
        const next = (prev + 1) % HERO_DATA.length;
        setPrevIndex(prev);
        setIsAnimating(true);
        setProgress(0);
        startTimeRef.current = performance.now();
        setTimeout(() => {
          setIsAnimating(false);
          setPrevIndex(null);
        }, 900);
        return next;
      });
    }, SLIDE_DURATION);
    return () => clearInterval(intervalRef.current);
  }, []);

  const active = HERO_DATA[activeIndex];

  return (
    <section
      className={`ph-hero${mounted ? " ph-hero--mounted" : ""}`}
      aria-label="Hero"
    >
      {/* ── BACKGROUND SLIDES (all spool-focused) ── */}
      <div className="ph-bg-stage" aria-hidden="true">
        {HERO_DATA.map((item, idx) => (
          <div
            key={item.id}
            className={`ph-bg-slide${idx === activeIndex ? " ph-bg-slide--active" : ""}${idx === prevIndex ? " ph-bg-slide--exit" : ""}`}
            style={{ backgroundImage: `url(${item.image})` }}
          />
        ))}

        {/* Dark gradient for text legibility */}
        <div className="ph-bg-gradient" />

        {/* Noise texture overlay */}
        <div className="ph-noise" aria-hidden="true" />
      </div>

      {/* ── CONTENT ── */}
      <div className="ph-content">
        {/* Top bar */}
        <div className="ph-topbar">
          <div className="ph-topbar-left">
            <span className="ph-slide-counter">
              <em>{active.id}</em> / {String(HERO_DATA.length).padStart(2, "0")}
            </span>
          </div>
        </div>

        {/* Main text */}
        <div className="ph-main">
          <div className="ph-text-area">
            {HERO_DATA.map((item, idx) => (
              <div
                key={item.id}
                className={`ph-text-block${idx === activeIndex ? " ph-text-block--active" : ""}`}
                aria-hidden={idx !== activeIndex}
              >
                <h1 className="ph-title" aria-live="polite">
                  {item.title.split("\n").map((line, i) => (
                    <span
                      key={i}
                      className="ph-title-line"
                      style={{ "--line-i": i }}
                    >
                      {/* Highlight "Arklon" everywhere it appears in titles with trademark */}
                      {line.includes("Arklon") ? (
                        <>
                          <span className="ph-brand-highlight">Arklon®</span>
                          {line.replace("Arklon", "")}
                        </>
                      ) : (
                        line
                      )}
                    </span>
                  ))}
                </h1>
                <p className="ph-desc">
                  {addTrademarkToArklon(item.desc)}
                </p>
              </div>
            ))}

            <div className="ph-cta-group">
              <a href={active.link} className="ph-btn-primary">
                Explore Lap Spools
                <svg
                  className="ph-btn-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom navigation */}
        <div className="ph-bottom">
          <div
            className="ph-slide-tabs"
            role="tablist"
            aria-label="Slide navigation"
          >
            {HERO_DATA.map((item, idx) => (
              <button
                key={item.id}
                className={`ph-tab${idx === activeIndex ? " ph-tab--active" : ""}`}
                role="tab"
                aria-selected={idx === activeIndex}
                aria-label={`Slide ${item.id}: ${addTrademarkToArklon(item.tag)}`}
                onClick={() => goTo(idx)}
              >
                <div className="ph-tab-track">
                  <div
                    className="ph-tab-fill"
                    style={{
                      width:
                        idx === activeIndex
                          ? `${progress}%`
                          : idx < activeIndex
                            ? "100%"
                            : "0%",
                    }}
                  />
                </div>
                <div className="ph-tab-body">
                  <span className="ph-tab-num">{item.id}</span>
                  <span className="ph-tab-label">
                    {addTrademarkToArklon(item.tag.includes("Arklon") ? item.tag : `Arklon ${item.tag}`)}
                  </span>
                </div>
              </button>
            ))}
          </div>

          <div className="ph-bottom-right">
            <span className="ph-year">Arklon® Est. 1997</span>
            <div className="ph-nav-arrows">
              <button
                className="ph-arrow"
                aria-label="Previous slide"
                onClick={() =>
                  goTo((activeIndex - 1 + HERO_DATA.length) % HERO_DATA.length)
                }
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <button
                className="ph-arrow"
                aria-label="Next slide"
                onClick={() => goTo((activeIndex + 1) % HERO_DATA.length)}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Vertical brand label — Arklon highlighted with trademark */}
      <div className="ph-vert-label" aria-hidden="true">
        <span>Emmark Industries • Arklon® LAP SPOOLS</span>
      </div>
    </section>
  );
}